// src/pages/Admin.jsx
import React, { useEffect, useState, useRef } from "react";
import { apiFetch, API_BASE } from "../utils/api";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [list, setList] = useState([]);
  const [lang, setLang] = useState("hr");

  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(false);
  const [editSlugOriginal, setEditSlugOriginal] = useState("");

  const [imageFile, setImageFile] = useState(null);
  const [offer, setOffer] = useState({ size: "90x200", price: 0, availability: "InStock" });

  const [msg, setMsg] = useState("");
  const [detail, setDetail] = useState(null); // {images:[], offers:[], ...}

  // DnD state za slike
  const [imagesLocal, setImagesLocal] = useState([]);
  const dragIndexRef = useRef(null);

  useEffect(() => { loadPublic(); }, [lang]);

  function emptyForm() {
    return {
      slug: "",
      title: "",
      description_html: "",
      price_from: 0,
      height: "",
      firmness: "",
      core: "",
      comfort: "",
      cover: "",
      warranty_years: 10,
      tags: "",
    };
  }

  async function loadPublic() {
    try {
      const data = await apiFetch(`/${lang}/products`);
      setList(data || []);
      setMsg("");
    } catch (e) {
      console.error(e);
      setMsg("❌ Greška pri dohvaćanju proizvoda.");
    }
  }

  async function fetchProductDetail(slug) {
    try {
      if (!slug) {
        setDetail(null);
        setImagesLocal([]);
        return;
      }
      const data = await apiFetch(`/${lang}/products/${encodeURIComponent(slug)}`);
      setDetail(data || null);
      setImagesLocal((data?.images || []).slice()); // lokalna kopija za drag
    } catch (e) {
      console.error(e);
      setDetail(null);
      setImagesLocal([]);
    }
  }

  function h(e) {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  }

  // ---------- CREATE ----------
  async function createProduct() {
    try {
      const body = normalizeForm(form);
      const res = await fetch(`${API_BASE}/admin/products`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.message || "Greška prilikom unosa");
      setMsg("✅ Proizvod dodan.");
      setForm(emptyForm());
      await loadPublic();
    } catch (e) {
      setMsg(`❌ ${e.message}`);
    }
  }

  // ---------- UPDATE ----------
  async function updateProduct() {
    try {
      if (!editSlugOriginal) throw new Error("Nije odabran proizvod za uređivanje");
      const body = normalizeForm(form);
      const res = await fetch(`${API_BASE}/admin/products/${encodeURIComponent(editSlugOriginal)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.message || "Greška prilikom ažuriranja");
      setMsg("✅ Promjene spremljene.");

      const newSlug = data?.slug || form.slug;
      setEditing(false);
      setEditSlugOriginal("");
      setForm(emptyForm());
      setDetail(null);
      setImagesLocal([]);
      await loadPublic();
      // opcionalno: možeš odmah otvoriti novi slug u editu
      // const updated = (await apiFetch(`/${lang}/products`)).find(p => p.slug === newSlug);
      // if (updated) selectForEdit(updated);
    } catch (e) {
      setMsg(`❌ ${e.message}`);
    }
  }

  // Pretvori stringove u brojke gdje treba, tagove u niz
  function normalizeForm(src) {
    return {
      ...src,
      price_from: Number(src.price_from || 0),
      height: src.height !== "" ? Number(src.height) : null,
      warranty_years: src.warranty_years !== "" ? Number(src.warranty_years) : null,
      tags: src.tags
        ? src.tags.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
    };
  }

  // ---------- SELECT FOR EDIT ----------
  async function selectForEdit(p) {
    setForm({
      slug: p.slug || "",
      title: p.title || "",
      description_html: p.description_html || "",
      price_from: p.price_from ?? 0,
      height: p.height ?? "",
      firmness: p.firmness || "",
      core: p.core || "",
      comfort: p.comfort || "",
      cover: p.cover || "",
      warranty_years: p.warranty_years ?? 10,
      tags: Array.isArray(p.tags) ? p.tags.join(", ") : "",
    });
    setEditSlugOriginal(p.slug || "");
    setEditing(true);
    setMsg("✏️ Uređuješ: " + (p.title || p.slug));
    await fetchProductDetail(p.slug);
  }

  function cancelEdit() {
    setEditing(false);
    setEditSlugOriginal("");
    setForm(emptyForm());
    setDetail(null);
    setImagesLocal([]);
    setMsg("✔️ Uređivanje otkazano.");
  }

  // ---------- IMAGE UPLOAD ----------
  async function uploadImage() {
    if (!form.slug) return setMsg("⚠️ Unesi slug proizvoda.");
    if (!imageFile) return setMsg("⚠️ Odaberi sliku prije uploada.");

    const fd = new FormData();
    fd.append("file", imageFile);
    try {
      const res = await fetch(`${API_BASE}/admin/products/${encodeURIComponent(form.slug)}/images`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.message || "Greška prilikom uploada slike");
      setMsg("✅ Slika dodana.");
      setImageFile(null);
      await fetchProductDetail(form.slug);
      await loadPublic();
    } catch (e) {
      setMsg(`❌ ${e.message}`);
    }
  }

  // ---------- IMAGES ACTIONS ----------
  async function setThumbnail(imageId) {
    try {
      const res = await fetch(`${API_BASE}/admin/products/${encodeURIComponent(form.slug)}/images/${imageId}/thumbnail`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.message || "Greška kod postavljanja thumbnaila");
      setMsg("⭐ Thumbnail postavljen.");
      await fetchProductDetail(form.slug);
      await loadPublic();
    } catch (e) {
      setMsg(`❌ ${e.message}`);
    }
  }

  async function deleteImage(imageId) {
    if (!confirm("Obrisati sliku?")) return;
    try {
      const res = await fetch(`${API_BASE}/admin/products/${encodeURIComponent(form.slug)}/images/${imageId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.message || "Greška kod brisanja slike");
      setMsg("🗑️ Slika obrisana.");
      await fetchProductDetail(form.slug);
      await loadPublic();
    } catch (e) {
      setMsg(`❌ ${e.message}`);
    }
  }

  // Drag & Drop handlers
  function onDragStart(idx) {
    dragIndexRef.current = idx;
  }
  function onDragOver(e) {
    e.preventDefault();
  }
  function onDrop(idx) {
    const from = dragIndexRef.current;
    if (from === null || from === idx) return;
    const arr = imagesLocal.slice();
    const [moved] = arr.splice(from, 1);
    arr.splice(idx, 0, moved);
    dragIndexRef.current = null;
    setImagesLocal(arr);
  }

  async function saveImagesOrder() {
    try {
      if (!form.slug) return;
      const order = imagesLocal.map(img => img.id);
      const res = await fetch(`${API_BASE}/admin/products/${encodeURIComponent(form.slug)}/images/reorder`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ order }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.message || "Greška kod spremanja poretka");
      setMsg("✅ Poredak slika spremljen.");
      await fetchProductDetail(form.slug);
      await loadPublic();
    } catch (e) {
      setMsg(`❌ ${e.message}`);
    }
  }

  // ---------- OFFERS ----------
  async function addOffer() {
    if (!form.slug) return setMsg("⚠️ Unesi slug proizvoda.");
    try {
      const res = await fetch(`${API_BASE}/admin/products/${encodeURIComponent(form.slug)}/offers`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...offer, price: Number(offer.price) }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.message || "Greška prilikom dodavanja veličine");
      setMsg("✅ Veličina dodana.");
      await fetchProductDetail(form.slug);
      await loadPublic();
    } catch (e) {
      setMsg(`❌ ${e.message}`);
    }
  }

  async function moveOffer(offerId, direction) {
    try {
      const res = await fetch(`${API_BASE}/admin/products/${encodeURIComponent(form.slug)}/offers/${offerId}/move`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ direction }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.message || "Greška kod pomaka veličine");
      await fetchProductDetail(form.slug);
      setMsg(direction === "up" ? "🔼 Veličina pomaknuta gore." : "🔽 Veličina pomaknuta dolje.");
    } catch (e) {
      setMsg(`❌ ${e.message}`);
    }
  }

  async function updateOffer(offer) {
    // minimalni inline update (cijena/dostupnost)
    try {
      const res = await fetch(`${API_BASE}/admin/products/${encodeURIComponent(form.slug)}/offers/${offer.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          size: offer.size,
          price: Number(offer.price_cents) / 100,
          availability: offer.availability,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.message || "Greška kod ažuriranja veličine");
      setMsg("✅ Veličina ažurirana.");
      await fetchProductDetail(form.slug);
    } catch (e) {
      setMsg(`❌ ${e.message}`);
    }
  }

  async function deleteOfferRow(offerId) {
    if (!confirm("Obrisati veličinu?")) return;
    try {
      const res = await fetch(`${API_BASE}/admin/products/${encodeURIComponent(form.slug)}/offers/${offerId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.message || "Greška kod brisanja veličine");
      setMsg("🗑️ Veličina obrisana.");
      await fetchProductDetail(form.slug);
      await loadPublic();
    } catch (e) {
      setMsg(`❌ ${e.message}`);
    }
  }

  // ---------- DELETE PRODUCT ----------
  async function del(slug) {
    if (!confirm(`Obrisati ${slug}?`)) return;
    try {
      const res = await fetch(`${API_BASE}/admin/products/${encodeURIComponent(slug)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      await res.json();
      setMsg("🗑️ Proizvod obrisan.");
      if (editing && editSlugOriginal === slug) cancelEdit();
      await loadPublic();
    } catch (e) {
      setMsg(`❌ ${e.message}`);
    }
  }

  return (
    <main className="max-w-6xl mx-auto px-6 py-8 text-white">
      <h1 className="text-3xl font-semibold text-gold mb-4">Admin panel</h1>

      {/* Token */}
      <div className="rounded-xl border border-white/10 bg-white/5 p-4 mb-6">
        <label className="block text-sm text-gray-300 mb-2">JWT token</label>
        <input
          className="w-full h-10 rounded-lg bg-white/5 border border-white/10 px-3"
          value={token}
          onChange={(e) => {
            setToken(e.target.value);
            localStorage.setItem("token", e.target.value);
          }}
          placeholder="Bearer token…"
        />
      </div>

      {/* Lang selector */}
      <div className="flex items-center gap-3 mb-6">
        <label className="text-sm text-gray-300">Jezik:</label>
        <select
          className="h-9 rounded-lg bg-white/5 border border-white/10 px-3"
          value={lang}
          onChange={(e) => setLang(e.target.value)}
        >
          <option value="hr">HR</option>
          <option value="en">EN</option>
        </select>
      </div>

      {/* Create / Edit form */}
      <div className="grid md:grid-cols-2 gap-6">
        <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">
              {editing ? "✏️ Uredi proizvod" : "🆕 Novi proizvod"}
            </h2>
            {editing && (
              <button
                onClick={cancelEdit}
                className="h-9 px-4 rounded-full border border-white/20 hover:border-gold hover:text-gold text-sm"
              >
                Odustani
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input name="slug" label="Slug" value={form.slug} onChange={h} />
            <Input name="title" label="Naziv" value={form.title} onChange={h} />
            <Input name="price_from" label="Cijena od (€)" value={form.price_from} onChange={h} />
            <Input name="height" label="Visina (cm)" value={form.height} onChange={h} />
            <Input name="firmness" label="Tvrdoća" value={form.firmness} onChange={h} />
            <Input name="warranty_years" label="Garancija (god)" value={form.warranty_years} onChange={h} />
            <Input name="core" label="Jezgra" value={form.core} onChange={h} />
            <Input name="comfort" label="Komfor" value={form.comfort} onChange={h} />
            <Input name="cover" label="Navlaka" value={form.cover} onChange={h} />
            <Input name="tags" label="Tagovi (zarezom)" value={form.tags} onChange={h} />
          </div>

          <label className="block text-sm text-gray-300 mt-3">Opis</label>
          <textarea
            name="description_html"
            value={form.description_html}
            onChange={h}
            className="w-full min-h-[90px] rounded-lg bg-white/5 border border-white/10 p-3"
          />
          <div className="mt-3 flex gap-3">
            {!editing ? (
              <button
                onClick={createProduct}
                className="h-11 px-5 rounded-full bg-gold text-black font-semibold hover:bg-gold-dark"
              >
                Spremi proizvod
              </button>
            ) : (
              <button
                onClick={updateProduct}
                className="h-11 px-5 rounded-full bg-gold text-black font-semibold hover:bg-gold-dark"
              >
                Spremi promjene
              </button>
            )}
          </div>
        </section>

        {/* Upload + Offer */}
        <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <h2 className="font-semibold mb-3">📸 Slika & veličine</h2>
          <div className="grid grid-cols-2 gap-3">
            <Input
              name="slugTarget"
              label="Slug (meta)"
              value={form.slug}
              onChange={(e) => setForm((s) => ({ ...s, slug: e.target.value }))}
            />
            <div>
              <label className="block text-sm text-gray-300 mb-1">Slika</label>
              <input
                type="file"
                onChange={(e) => setImageFile(e.target.files?.[0])}
                className="block w-full text-sm"
              />
              <button
                onClick={uploadImage}
                className="mt-2 h-10 px-4 rounded-full border border-white/20 hover:border-gold hover:text-gold"
              >
                Upload sliku
              </button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-3">
            <Input
              label="Veličina"
              value={offer.size}
              onChange={(e) => setOffer((s) => ({ ...s, size: e.target.value }))}
            />
            <Input
              label="Cijena (€)"
              value={offer.price}
              onChange={(e) => setOffer((s) => ({ ...s, price: e.target.value }))}
            />
            <select
              className="h-10 rounded-lg bg-white/5 border border-white/10 px-3"
              value={offer.availability}
              onChange={(e) => setOffer((s) => ({ ...s, availability: e.target.value }))}
            >
              <option>InStock</option>
              <option>OutOfStock</option>
            </select>
          </div>
          <button
            onClick={addOffer}
            className="mt-2 h-10 px-4 rounded-full bg-gold text-black font-semibold hover:bg-gold-dark"
          >
            Dodaj veličinu
          </button>
        </section>
      </div>

      {/* Images panel (vidljiv kad je u editu) */}
      {editing && (
        <section className="rounded-2xl border border-white/10 bg-white/5 p-4 mt-6">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold mb-3">🖼️ Slike proizvoda ({imagesLocal.length})</h2>
            <button
              onClick={saveImagesOrder}
              className="h-9 px-4 rounded-full bg-gold text-black font-semibold hover:bg-gold-dark"
            >
              Spremi poredak
            </button>
          </div>

          {!imagesLocal.length ? (
            <div className="text-sm text-gray-400">Nema slika za ovaj proizvod.</div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {imagesLocal.map((img, idx) => (
                <article
                  key={img.id}
                  className="rounded-xl overflow-hidden border border-white/10 bg-white/5"
                  draggable
                  onDragStart={() => onDragStart(idx)}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(idx)}
                  title="Povuci za promjenu poretka"
                >
                  <img src={img.url} alt={img.alt || form.title || form.slug} className="h-44 w-full object-cover" />
                  <div className="p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="text-gray-300">
                        {idx === 0 ? <span className="text-gold">★ Thumbnail</span> : <span>Slika</span>}
                      </div>
                      <span className="text-xs text-gray-500">#{img.id}</span>
                    </div>
                    <div className="mt-2 flex gap-2">
                      {idx !== 0 && (
                        <button
                          onClick={() => setThumbnail(img.id)}
                          className="h-8 px-3 rounded-full border border-white/20 hover:border-gold hover:text-gold"
                        >
                          Postavi thumbnail
                        </button>
                      )}
                      <button
                        onClick={() => deleteImage(img.id)}
                        className="h-8 px-3 rounded-full border border-red-400/40 text-red-300 hover:border-red-400 hover:text-red-200"
                      >
                        Obriši
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {/* OFFERS panel (vidljiv kad je u editu) */}
      {editing && (
        <section className="rounded-2xl border border-white/10 bg-white/5 p-4 mt-6">
          <h2 className="font-semibold mb-3">📏 Veličine (offers) — {detail?.offers?.length || 0}</h2>
          {!detail?.offers?.length ? (
            <div className="text-sm text-gray-400">Nema veličina. Dodaj gore.</div>
          ) : (
            <div className="space-y-2">
              {detail.offers.map((o, idx) => (
                <div
                  key={o.id}
                  className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 p-2"
                >
                  <span className="text-xs text-gray-400 w-10 text-center">#{o.id}</span>
                  <input
                    className="h-9 w-28 rounded-lg bg-white/5 border border-white/10 px-2"
                    value={o.size}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDetail(d => ({
                        ...d,
                        offers: d.offers.map(x => x.id === o.id ? { ...x, size: v } : x)
                      }));
                    }}
                    placeholder="Veličina"
                  />
                  <input
                    className="h-9 w-28 rounded-lg bg-white/5 border border-white/10 px-2"
                    value={(Number(o.price_cents || 0) / 100).toFixed(2)}
                    onChange={(e) => {
                      const v = e.target.value;
                      const cents = Math.round(Number(v || 0) * 100);
                      setDetail(d => ({
                        ...d,
                        offers: d.offers.map(x => x.id === o.id ? { ...x, price_cents: cents } : x)
                      }));
                    }}
                    placeholder="Cijena (€)"
                  />
                  <select
                    className="h-9 rounded-lg bg-white/5 border border-white/10 px-2"
                    value={o.availability}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDetail(d => ({
                        ...d,
                        offers: d.offers.map(x => x.id === o.id ? { ...x, availability: v } : x)
                      }));
                    }}
                  >
                    <option>InStock</option>
                    <option>OutOfStock</option>
                  </select>

                  <div className="ml-auto flex items-center gap-2">
                    <button
                      onClick={() => moveOffer(o.id, "up")}
                      disabled={idx === 0}
                      className="h-8 px-3 rounded-full border border-white/20 hover:border-gold hover:text-gold disabled:opacity-40"
                      title="Gore"
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => moveOffer(o.id, "down")}
                      disabled={idx === (detail.offers.length - 1)}
                      className="h-8 px-3 rounded-full border border-white/20 hover:border-gold hover:text-gold disabled:opacity-40"
                      title="Dolje"
                    >
                      ▼
                    </button>
                    <button
                      onClick={() => updateOffer(o)}
                      className="h-8 px-3 rounded-full bg-gold text-black font-semibold hover:bg-gold-dark"
                    >
                      Spremi
                    </button>
                    <button
                      onClick={() => deleteOfferRow(o.id)}
                      className="h-8 px-3 rounded-full border border-red-400/40 text-red-300 hover:border-red-400 hover:text-red-200"
                    >
                      Obriši
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Product list */}
      <h2 className="mt-8 mb-3 font-semibold">📦 Proizvodi ({list.length})</h2>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
        {list.map((p) => (
          <article key={p.slug} className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
            {p.thumbnail ? (
              <img src={p.thumbnail} alt={p.title} className="h-40 w-full object-cover" />
            ) : (
              <div className="h-40 w-full bg-white/5" />
            )}
            <div className="p-4">
              <div className="font-semibold">{p.title}</div>
              <div className="text-sm text-gray-400">
                {p.slug} · {p.height ? `${p.height} cm · ` : ""}{p.firmness || "—"}
              </div>
              <div className="text-gold font-semibold mt-1">
                {Number(p.price_from).toFixed(0)} €
              </div>

              <div className="mt-3 flex gap-2">
                <a
                  className="h-9 px-3 inline-flex items-center rounded-full border border-white/20 hover:border-gold hover:text-gold text-sm"
                  href={`/${lang}/proizvod/${p.slug}`}
                  target="_blank"
                >
                  Otvori
                </a>
                <button
                  onClick={() => selectForEdit(p)}
                  className="h-9 px-3 inline-flex items-center rounded-full border border-white/20 hover:border-gold hover:text-gold text-sm"
                >
                  Uredi
                </button>
                <button
                  onClick={() => del(p.slug)}
                  className="h-9 px-3 inline-flex items-center rounded-full border border-white/20 hover:border-gold hover:text-gold text-sm"
                >
                  Obriši
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {!!msg && <div className="mt-6 text-sm text-gray-300">{msg}</div>}
    </main>
  );
}

function Input({ label, name, value, onChange }) {
  return (
    <div>
      {label && <label className="block text-sm text-gray-300 mb-1">{label}</label>}
      <input
        name={name}
        value={value}
        onChange={onChange}
        className="w-full h-10 rounded-lg bg-white/5 border border-white/10 px-3"
      />
    </div>
  );
}
